import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAbortableLoad } from '../hooks/useAbortableLoad';
import { tienePermiso } from '../utils/navPermisos';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Plus, Search, Edit, Trash2, Loader2, Users as UsersIcon, 
  AlertCircle, Shield, Eye, EyeOff, ChevronRight, KeyRound, Building2, Wrench, ShieldAlert
} from 'lucide-react';
import { Switch } from '../components/ui/switch';

const ROLE_COLORS = {
  ADMINISTRADOR: 'bg-purple-100 text-purple-800',
  SUPERVISOR: 'bg-blue-100 text-blue-800',
  LEGAL: 'bg-teal-100 text-teal-800',
  USUARIO: 'bg-slate-100 text-slate-800',
};

const UsuariosPage = () => {
  const { api, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const themeColors = useThemeColors();
  const puedeCrear = tienePermiso(currentUser, 'usuarios', 'crear');
  const puedeEditar = tienePermiso(currentUser, 'usuarios', 'editar');
  const puedeEliminar = tienePermiso(currentUser, 'usuarios', 'eliminar');
  const puedeGestionar = puedeCrear || puedeEditar || puedeEliminar;
  const puedeVerRoles = tienePermiso(currentUser, 'roles', 'ver');
  const puedeVerUsuarios = tienePermiso(currentUser, 'usuarios', 'ver');
  const esAdmin = ['ADMINISTRADOR', 'SUPERADMIN'].includes(currentUser?.rol);
  // Un usuario objetivo con rol ADMINISTRADOR/SUPERADMIN solo puede ser gestionado por un admin/superadmin.
  const puedeGestionarObjetivo = (u) =>
    esAdmin || !['ADMINISTRADOR', 'SUPERADMIN'].includes(u?.rol);
  const [loading, setLoading] = useState(true);
  const [usuarios, setUsuarios] = useState([]);
  const [areas, setAreas] = useState([]);
  const [roles, setRoles] = useState([]);
  const [search, setSearch] = useState('');
  const [filterTecnico, setFilterTecnico] = useState('todos'); // 'todos' | 'tecnicos' | 'no_tecnicos'
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    password: '',
    rol_id: '',
    cambio_password_requerido: false,
    activo: true,
    area_funcional_id: '',
    es_tecnico: false,
  });

  const defaultRolId = () => {
    const usuario = roles.find(r => r.nombre === 'USUARIO');
    return usuario?.id || roles[0]?.id || '';
  };

  const fetchUsuarios = async (signal) => {
    setLoading(true);
    try {
      const response = await api.get('/usuarios', { signal });
      setUsuarios(response.data);
    } catch (err) {
      console.error('Error fetching usuarios:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAreas = async (signal) => {
    try {
      const response = await api.get('/areas-funcionales?incluir_inactivas=false', { signal });
      setAreas(response.data || []);
    } catch (err) {
      setAreas([]);
    }
  };

  const fetchRoles = async (signal) => {
    try {
      const response = await api.get('/roles', { signal });
      setRoles(response.data || []);
    } catch (err) {
      setRoles([]);
    }
  };

  useAbortableLoad((signal) => {
    fetchUsuarios(signal);
    fetchAreas(signal);
    fetchRoles(signal);
  }, []);

  const openCreate = () => {
    setEditingUsuario(null);
    setFormData({
      nombre: '',
      correo: '',
      password: '',
      rol_id: defaultRolId(),
      cambio_password_requerido: false,
      activo: true,
      area_funcional_id: '',
      es_tecnico: false,
    });
    setError('');
    setShowPassword(false);
    setDialogOpen(true);
  };

  const openEdit = (usuario) => {
    setEditingUsuario(usuario);
    setFormData({
      nombre: usuario.nombre,
      correo: usuario.correo,
      password: '',
      rol_id: usuario.rol_id || roles.find(r => r.nombre === usuario.rol)?.id || '',
      cambio_password_requerido: usuario.cambio_password_requerido || false,
      activo: usuario.activo !== false,
      area_funcional_id: usuario.area_funcional_id || '',
      es_tecnico: usuario.es_tecnico || false,
    });
    setError('');
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const data = { ...formData };
      if (editingUsuario && !data.password) {
        delete data.password;
      }
      if (editingUsuario) {
        await api.put(`/usuarios/${editingUsuario.id}`, data);
      } else {
        await api.post('/usuarios', data);
      }
      setDialogOpen(false);
      fetchUsuarios();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivo = async (usuario) => {
    if (usuario.id === currentUser.id) {
      alert('No puede desactivarse a sí mismo');
      return;
    }
    if (usuario.activo) {
      if (!window.confirm(`¿Está seguro de desactivar al usuario "${usuario.nombre}"?`)) return;
    }
    try {
      await api.put(`/usuarios/${usuario.id}`, { activo: !usuario.activo });
      fetchUsuarios();
    } catch (err) {
      console.error('Error toggling usuario:', err);
    }
  };

  const getRolBadge = (usuario) => {
    const nombre = roles.find(r => r.id === usuario.rol_id)?.nombre || usuario.rol;
    return (
      <Badge className={ROLE_COLORS[usuario.rol] || 'bg-slate-100 text-slate-800'}>
        {nombre}
      </Badge>
    );
  };

  const filteredUsuarios = usuarios.filter(u => {
    const matchTexto = u.nombre.toLowerCase().includes(search.toLowerCase()) ||
      u.correo.toLowerCase().includes(search.toLowerCase());
    const matchTecnico = filterTecnico === 'todos'
      || (filterTecnico === 'tecnicos' && u.es_tecnico === true)
      || (filterTecnico === 'no_tecnicos' && !u.es_tecnico);
    return matchTexto && matchTecnico;
  });

  const areasMap = areas.reduce((acc, a) => { acc[a.id] = a.nombre; return acc; }, {});

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="usuarios-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Usuarios</h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestión de usuarios del sistema
          </p>
        </div>
        {puedeGestionar && (
          <div className="flex flex-col sm:flex-row gap-2">
            {puedeVerRoles && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin/roles')}
                data-testid="roles-link"
              >
                <Shield className="w-4 h-4 mr-2" />
                Roles
              </Button>
            )}
            {esAdmin && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin/areas-funcionales')}
                data-testid="areas-funcionales-link"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Áreas funcionales
              </Button>
            )}
            {puedeVerUsuarios && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin/usuarios/bloqueados')}
                data-testid="cuentas-bloqueadas-link"
              >
                <ShieldAlert className="w-4 h-4 mr-2" />
                Cuentas bloqueadas
              </Button>
            )}
            {puedeCrear && (
              <Button 
                onClick={openCreate} 
                data-testid="crear-usuario-btn" 
                className="text-white"
                {...getPrimaryButtonStyles(themeColors)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Usuario
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Search + filtro técnico */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o correo..."
                data-testid="busqueda-input"
                className="pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={filterTecnico} onValueChange={setFilterTecnico}>
              <SelectTrigger className="w-full sm:w-52 shrink-0" data-testid="filtro-tecnico-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los usuarios</SelectItem>
                <SelectItem value="tecnicos">Solo técnicos</SelectItem>
                <SelectItem value="no_tecnicos">No técnicos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Data View - Responsive */}
      {loading ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          </CardContent>
        </Card>
      ) : filteredUsuarios.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <UsersIcon className="w-12 h-12 mb-4 text-slate-300" />
              <p>No se encontraron usuarios</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Vista móvil - Cards */}
          <div className="md:hidden space-y-3">
            {filteredUsuarios.map((usuario) => (
              <Card 
                key={usuario.id}
                className={`border-slate-200 ${usuario.id === currentUser.id ? 'ring-2 ring-blue-200 bg-blue-50/50' : ''}`}
                data-testid={`usuario-card-${usuario.id}`}
              >
                <CardContent className="p-4">
                  {/* Header: Nombre + Badges */}
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{usuario.nombre}</p>
                        {usuario.id === currentUser.id && (
                          <Badge variant="outline" className="text-xs">Tú</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{usuario.correo}</p>
                    </div>
                    <div className="flex-shrink-0">
                      {getRolBadge(usuario)}
                    </div>
                  </div>
                  
                  {/* Datos adicionales */}
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">2FA:</span>
                      {usuario.two_factor_enabled ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge>
                      ) : (
                        <Badge variant="outline">Inactivo</Badge>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Estado:</span>
                      <Badge className={usuario.activo ? 'status-terminado' : 'bg-red-100 text-red-800'}>
                        {usuario.activo ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Técnico:</span>
                      {usuario.es_tecnico ? (
                        <Badge className="bg-indigo-100 text-indigo-800 gap-1"><Wrench className="w-3 h-3" />Sí</Badge>
                      ) : (
                        <Badge variant="outline">No</Badge>
                      )}
                    </div>
                  </div>
                  
                  {/* Actions */}
                  {(puedeEditar || puedeEliminar) && puedeGestionarObjetivo(usuario) && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end gap-2">
                      {puedeEditar && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openEdit(usuario)}
                          data-testid={`editar-usuario-mobile-${usuario.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      )}
                      {puedeEliminar && usuario.id !== currentUser.id && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleToggleActivo(usuario)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Vista desktop - Tabla */}
          <Card className="border-slate-200 hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Nombre</TableHead>
                      <TableHead>Correo</TableHead>
                      <TableHead>Rol</TableHead>
                      <TableHead>Área funcional</TableHead>
                      <TableHead>Técnico</TableHead>
                      <TableHead>2FA</TableHead>
                      <TableHead>Estado</TableHead>
                      {(puedeEditar || puedeEliminar) && <TableHead></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsuarios.map((usuario) => (
                      <TableRow 
                        key={usuario.id} 
                        className={`table-row ${usuario.id === currentUser.id ? 'bg-blue-50' : ''}`}
                        data-testid={`usuario-row-${usuario.id}`}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {usuario.nombre}
                            {usuario.id === currentUser.id && (
                              <Badge variant="outline" className="text-xs">Tú</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{usuario.correo}</TableCell>
                        <TableCell>{getRolBadge(usuario)}</TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {areasMap[usuario.area_funcional_id] || '—'}
                        </TableCell>
                        <TableCell data-testid={`tecnico-badge-${usuario.id}`}>
                          {usuario.es_tecnico ? (
                            <Badge className="bg-indigo-100 text-indigo-800 gap-1"><Wrench className="w-3 h-3" />Técnico</Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {usuario.two_factor_enabled ? (
                            <Badge className="bg-emerald-100 text-emerald-800">Activo</Badge>
                          ) : (
                            <Badge variant="outline">Inactivo</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={usuario.activo ? 'status-terminado' : 'bg-red-100 text-red-800'}>
                            {usuario.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        {(puedeEditar || puedeEliminar) && (
                          <TableCell>
                            <div className="flex gap-1">
                              {puedeEditar && puedeGestionarObjetivo(usuario) && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openEdit(usuario)}
                                  data-testid={`editar-usuario-${usuario.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              {puedeEliminar && puedeGestionarObjetivo(usuario) && usuario.id !== currentUser.id && (
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => handleToggleActivo(usuario)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md w-[95vw] p-0 gap-0 flex flex-col max-h-[90dvh] overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-slate-100">
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {editingUsuario ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre Completo *</Label>
              <Input
                id="nombre"
                data-testid="nombre-input"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Juan Pérez"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="correo">Correo Electrónico *</Label>
              <Input
                id="correo"
                type="email"
                data-testid="correo-input"
                value={formData.correo}
                onChange={(e) => setFormData({ ...formData, correo: e.target.value })}
                placeholder="usuario@empresa.com"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">
                {editingUsuario ? 'Nueva Contraseña (dejar vacío para mantener)' : 'Contraseña *'}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required={!editingUsuario}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <PasswordStrengthIndicator password={formData.password} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rol">Rol *</Label>
              <Select
                value={formData.rol_id}
                onValueChange={(value) => setFormData({ ...formData, rol_id: value })}
              >
                <SelectTrigger id="rol" data-testid="rol-select" className="w-full min-w-0">
                  <SelectValue placeholder="Seleccione un rol" className="truncate" />
                </SelectTrigger>
                <SelectContent>
                  {roles
                    .filter((rol) => esAdmin || !['ADMINISTRADOR', 'SUPERADMIN'].includes(rol.nombre))
                    .map((rol) => (
                      <SelectItem key={rol.id} value={rol.id} className="truncate">
                        {rol.nombre}{rol.es_sistema ? '' : ' (personalizado)'}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="area_funcional">Área funcional</Label>
              <Select
                value={formData.area_funcional_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, area_funcional_id: value === '__none__' ? '' : value })}
              >
                <SelectTrigger id="area_funcional" data-testid="area-funcional-select" className="w-full min-w-0">
                  <SelectValue placeholder="Sin área asignada" className="truncate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin área asignada</SelectItem>
                  {areas.map((a) => (
                    <SelectItem key={a.id} value={a.id} className="truncate max-w-[var(--radix-select-trigger-width)]">{a.nombre} — {a.gerencia}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-slate-500" />
                <Label htmlFor="cambio_password" className="text-sm font-normal cursor-pointer">
                  Solicitar cambio de contraseña en el próximo inicio de sesión
                </Label>
              </div>
              <Switch
                id="cambio_password"
                data-testid="cambio-password-switch"
                checked={formData.cambio_password_requerido}
                onCheckedChange={(checked) => setFormData({ ...formData, cambio_password_requerido: checked })}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-500" />
                <Label htmlFor="activo" className="text-sm font-normal cursor-pointer">
                  Usuario activo
                </Label>
              </div>
              <Switch
                id="activo"
                data-testid="activo-switch"
                checked={formData.activo}
                onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div className="flex items-start gap-2 min-w-0">
                <Wrench className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <Label htmlFor="es_tecnico" className="text-sm font-normal cursor-pointer">
                    Es técnico
                  </Label>
                  <p className="text-xs text-slate-500">Identifica al personal técnico de atención al cliente (para reportes de BI).</p>
                </div>
              </div>
              <Switch
                id="es_tecnico"
                data-testid="es-tecnico-switch"
                checked={formData.es_tecnico}
                onCheckedChange={(checked) => setFormData({ ...formData, es_tecnico: checked })}
              />
            </div>
            </div>

            <DialogFooter className="px-6 py-4 shrink-0 border-t border-slate-100 flex-col-reverse sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="w-full sm:w-auto">
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                data-testid="guardar-usuario-btn"
                className="text-white w-full sm:w-auto"
                {...getPrimaryButtonStyles(themeColors)}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingUsuario ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsuariosPage;
